import { OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Standard pitchIN API envelope: every response is `{ result: T, ... }`.
 *
 * Services MUST unwrap `.result` before handing data to components. Casting the
 * whole envelope (`map(r => r as T)`) compiles fine but hands components the
 * wrapper object instead of the payload — the screen then renders nothing while
 * the build stays green. That exact omission shipped to UAT (CorporateActionService).
 *
 * Use this operator instead of a hand-written `map(...)` so the unwrap can't be
 * forgotten and the intent is explicit:
 *
 *   return this.http.get<ApiEnvelope<Foo[]>>(url).pipe(unwrapResult());
 */
export interface ApiEnvelope<T> {
  result: T;
}

export function unwrapResult<T>(): OperatorFunction<ApiEnvelope<T>, T> {
  return map((response: ApiEnvelope<T>) => {
    // Strict on purpose: a missing envelope or absent `result` key is a contract
    // violation. Failing loudly (vs. optional-chaining to `undefined`) keeps the
    // "renders-but-broken" empty-screen class of bug from slipping through again.
    // Note: `result: null` is a valid payload (e.g. pending-vote), so we check for
    // the *presence* of the key, not its truthiness.
    if (response == null || !('result' in response)) {
      throw new Error('unwrapResult: API response is missing the `result` envelope.');
    }
    return response.result;
  });
}
